<template>
  <Loader
    v-if="isLoading"
    style="z-index:1102"
  />

  <div class="grid">
    <div class="col-12 flex flex-column">
      <div class="field">
        <label>{{ t('storeParameters.services.storeLogo') }}</label>
        <div class="flex justify-content-between flex-wrap mb-3">
          <div class="flex w-full">
            <ImageUploader
              v-model="field.shopDataSubscriptionPage_logoPath"
              :empty-label="t('storeParameters.services.subscriptionPageServiceParameter.fields.dragAndDropLogo')"
              :shop-id="shopId"
              @on-loading="handleOnLoading"
              @uploaded="handleUploaded"
            />
          </div>
        </div>
      </div>

      <div class="field">
        <label>{{ t('storeParameters.services.subscriptionPageServiceParameter.fields.buttonColor') }}</label>
        <SimpleColorPicker
          id="shopDataSubscriptionPage_colorButton"
          :selected-color="field.shopDataSubscriptionPage_colorButton"
          @color-changed="(color) => { field.shopDataSubscriptionPage_colorButton = color }"
        />
      </div>

      <div class="field">
        <label>{{ t('storeParameters.services.subscriptionPageServiceParameter.fields.languages') }}</label>
        <LanguageMultiDropdown
          :key="languageDropdownKey"
          v-model="field.shopDataSubscriptionPage_langs"
          :options="availableLanguages"
          :placeholder="t('storeParameters.services.subscriptionPageServiceParameter.fields.selectLang')"
          class="w-full"
        />
      </div>

      <div class="field flex align-items-center">
        <div>
          <label class="mb-0">
            {{ t('storeParameters.services.subscriptionPageServiceParameter.fields.showUnsubscribeReason') }}
          </label>
        </div>
        <SelectButton
          v-model="field.shopDataSubscriptionPage_showUnsubscribeReason"
          :options="yesno"
          :unselectable="false"
          option-label="name"
          option-value="value"
          class="ml-auto"
        />
      </div>

      <div class="field flex align-items-center">
        <div>
          <label class="mb-0">
            {{ t('storeParameters.services.subscriptionPageServiceParameter.fields.showTrackingClic') }}
          </label>
        </div>
        <SelectButton
          v-model="field.shopDataSubscriptionPage_showTrackingClic"
          :options="yesno"
          :unselectable="false"
          option-label="name"
          option-value="value"
          class="ml-auto"
        />
      </div>
    </div>

    <SpmButton
      v-if="!globalSave"
      :label="t('storeParameters.services.subscriptionPageServiceParameter.fields.save')"
      :loading="saveLoading"
      class="p-button p-button-primary"
      @click="handleSave"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, reactive, onMounted, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import SelectButton from 'primevue/selectbutton';
import Loader from '@/components/layout/Loader.vue';
import ImageUploader from '@/components/fields/ImageUploader.vue';
import SimpleColorPicker from '@/components/fields/SimpleColorPicker.vue';
import LanguageMultiDropdown from '@/components/fields/LanguageMultiDropdown.vue';
import useVuelidate from '@vuelidate/core';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import { saveShopParamsOnRedis, fetchShopConfigurationsNest, saveShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import { showToastError, showToastSuccess } from '@/helpers';
import { ShopsConfigurationInputItem } from '@/types/generated-types/graphql';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';

export default defineComponent({
  name: 'SubscriptionPageServiceParameter',

  components: {
    Loader,
    ImageUploader,
    SimpleColorPicker,
    LanguageMultiDropdown,
    SpmButton,
    SelectButton,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },

    name: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['close'],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const isLoading = ref(false);
    const isLogoLoading = ref(false);
    const error = ref();
    const componentFieldErrorsKey = ref(0);
    const saveLoading = ref(false);
    const languageDropdownKey = ref(0);

    const availableLanguages = [
      { label: 'Français', value: 'fr' },
      { label: 'English', value: 'en' },
      { label: 'Español', value: 'es' },
      { label: 'Italiano', value: 'it' },
      { label: 'Deutsch', value: 'de' },
      { label: 'Português', value: 'pt' },
    ];

    const yesno = [
      { name: t('yes'), value: 1 },
      { name: t('no'), value: 0 },
    ];

    const field = reactive<Record<string, any>>({
      shopDataSubscriptionPage_logoPath: '',
      shopDataSubscriptionPage_colorButton: '',
      shopDataSubscriptionPage_langs: [],
      shopDataSubscriptionPage_showUnsubscribeReason: 0,
      shopDataSubscriptionPage_showTrackingClic: 0,
      shopRequiredDataEmail_colorCta: '',
      shopRequiredDataEmail_logoPath: '',
    });

    const handleOnLoading = () => {
      isLogoLoading.value = true;
    };

    const handleUploaded = () => {
      isLogoLoading.value = false;
    };

    const validate = async (): Promise<ErrorConfigForm> => {
      const rules: Record<string, any> = {
        shopDataSubscriptionPage_logoPath: {
          required: (value: string) => !!value || 'Le logo est requis',
        },

        shopDataSubscriptionPage_colorButton: {
          required: (value: string) => !!value || 'La couleur du bouton est requise',
          color: (value: string) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value) || 'Format de couleur invalide',
        },

        shopDataSubscriptionPage_langs: {
          required: (value: string[]) => value.length > 0 || 'Au moins une langue doit être sélectionnée',
          validLanguages: (value: string[]) => {
            const validLanguages = ['fr', 'en', 'es', 'it', 'de', 'pt'];
            return value.every((lang) => validLanguages.includes(lang)) || 'Langue non valide';
          },
        },
      };

      const v$ = useVuelidate(rules, field);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const handleSave = async () => {
      const validation = await validate();
      if (!validation.success) {
        error.value = validation.validate;
        componentFieldErrorsKey.value += 1;
        return {
          err: validation.validate.value.$errors.length,
          configs: [],
        };
      }

      // Prepare configurations for Nest API
      const configurationsToSave: Array<{ key: string; value: string; lang?: string }> = [];

      // Filter out shopRequiredDataEmail fields before saving
      const fieldsToExclude = ['shopRequiredDataEmail_colorCta', 'shopRequiredDataEmail_logoPath'];
      const filteredFields = Object.fromEntries(
        Object.entries(field).filter(([key]) => !fieldsToExclude.includes(key)),
      );

      Object.entries(filteredFields).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Multi-language values
          Object.entries(value).forEach(([language, inputValue]) => {
            configurationsToSave.push({
              key,
              value: typeof inputValue === 'string' ? inputValue : String(inputValue),
              lang: language,
            });
          });
        } else if (Array.isArray(value)) {
          // Array values (stringify)
          configurationsToSave.push({
            key,
            value: JSON.stringify(value),
          });
        } else {
          // Simple values
          configurationsToSave.push({
            key,
            value: String(value),
          });
        }
      });

      if (props.globalSave) {
        // For global save, return GraphQL format for compatibility
        const shopsConfigArray: ShopsConfigurationInputItem[] = configurationsToSave.map((config) => ({
          key: config.key,
          value: config.value,
          lang: config.lang || '',
        }));
        return {
          err: null,
          configs: shopsConfigArray,
        };
      }

      saveLoading.value = true;

      try {
        const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
    };

    onMounted(async () => {
      try {
        isLoading.value = true;
        // Fetch configurations from Nest REST API
        const keys = Object.keys(field);
        const data = await fetchShopConfigurationsNest(props.shopId, keys);
        if (data) {
          // Map API response to field
          Object.keys(data).forEach((key) => {
            if (key in field) {
              field[key] = data[key];
            }
          });
        }

        // Parse shopDataSubscriptionPage_langs if it's a string
        if (field.shopDataSubscriptionPage_langs) {
          if (typeof field.shopDataSubscriptionPage_langs === 'string') {
            try {
              const parsedLangs = JSON.parse(field.shopDataSubscriptionPage_langs);
              field.shopDataSubscriptionPage_langs = parsedLangs;
              await nextTick();
              languageDropdownKey.value += 1;
            } catch (e) {
              field.shopDataSubscriptionPage_langs = [];
            }
          } else if (typeof field.shopDataSubscriptionPage_langs === 'object' && !Array.isArray(field.shopDataSubscriptionPage_langs)) {
            field.shopDataSubscriptionPage_langs = [];
          }
        } else {
          field.shopDataSubscriptionPage_langs = [];
        }

        // Convert showUnsubscribeReason to number
        if (field.shopDataSubscriptionPage_showUnsubscribeReason === '1' || field.shopDataSubscriptionPage_showUnsubscribeReason === true) {
          field.shopDataSubscriptionPage_showUnsubscribeReason = 1;
        } else {
          field.shopDataSubscriptionPage_showUnsubscribeReason = 0;
        }

        // Convert showTrackingClic to number
        if (field.shopDataSubscriptionPage_showTrackingClic === '1' || field.shopDataSubscriptionPage_showTrackingClic === true) {
          field.shopDataSubscriptionPage_showTrackingClic = 1;
        } else {
          field.shopDataSubscriptionPage_showTrackingClic = 0;
        }

        // Auto-fill from email settings if needed
        const configurationsToSave = [];
        let needsSave = false;

        if (!field.shopDataSubscriptionPage_logoPath && field.shopRequiredDataEmail_logoPath) {
          field.shopDataSubscriptionPage_logoPath = field.shopRequiredDataEmail_logoPath;
          configurationsToSave.push({
            key: 'shopDataSubscriptionPage_logoPath',
            value: field.shopDataSubscriptionPage_logoPath,
            lang: null,
          });
          needsSave = true;
        }

        if (!field.shopDataSubscriptionPage_colorButton && field.shopRequiredDataEmail_colorCta) {
          field.shopDataSubscriptionPage_colorButton = field.shopRequiredDataEmail_colorCta;
          configurationsToSave.push({
            key: 'shopDataSubscriptionPage_colorButton',
            value: field.shopDataSubscriptionPage_colorButton,
            lang: null,
          });
          needsSave = true;
        }

        // Save default values using Nest API
        if (needsSave && configurationsToSave.length > 0) {
          try {
            const result = await saveShopConfigurationsNest(props.shopId, configurationsToSave);
            if (result) {
              await saveShopParamsOnRedis(props.shopId);
            } else {
              showToastError('GENERIC_ERROR');
            }
          } catch (err) {
            showToastError('GENERIC_ERROR');
          }
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      isLoading,
      t,
      field,
      availableLanguages,
      yesno,
      handleOnLoading,
      handleUploaded,
      saveLoading,
      handleSave,
      languageDropdownKey,
    };
  },
});
</script>
